import { userCountryGeolocated } from '@actions/app.action';
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  createUrlTreeFromSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import {
  COUNTRY_CODE_TO_CULTURE,
  CULTURE_CODE_REGEX,
  CULTURE_US,
  DEFAULT_COUNTRY_CODE_BY_CULTURE,
  FALLBACK_CULTURE,
  SECONDS_PER_DAY,
} from '@constants/app.constants';
import { CultureResource } from '@models/resources/culture.model';
import { Store } from '@ngrx/store';
import { CoreState } from '@reducers/index';
import { selectCultures } from '@selectors/resource.selector';
import { GeocodeService } from '@services/geocode/geocode.service';
import {
  filter,
  Observable,
  of,
  switchMap,
  take,
  timeout,
} from 'rxjs';
import { CookieStorageService } from '@services/cookie-storage/cookie-storage.service';
import { concatLatestFrom } from '@ngrx/operators';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { resourceGetCulturesSuccess } from '@actions/resource.action';
import { selectCloudflareMeta } from '@selectors/app.selector';
import { CloudflareMeta } from '@models/app.models';

export const hasValidCultureRouteGuard = (
  route: ActivatedRouteSnapshot,
): Observable<boolean | UrlTree> => {
  const store = inject<Store<CoreState>>(Store);
  const geocodeService = inject(GeocodeService);
  const cookieService = inject(CookieStorageService);
  const cookieStorageService = inject(CookieStorageService);

  return store.select(selectCultures).pipe(
    filter((cultures) => !!cultures),
    timeout({
      each: +EnvironmentService.environment.appTimeoutMillis,
      with: () => {
        const cultureDefault = [
          { cultureCode: CULTURE_US, name: 'English (United States)' },
        ] as CultureResource[];
        store.dispatch(
          resourceGetCulturesSuccess({ cultures: cultureDefault }),
        );
        return of(cultureDefault);
      },
    }),
    concatLatestFrom(() => [
      store.select(selectCloudflareMeta),
    ]),
    take(1),
    switchMap(
      ([cultures, cloudflareMeta]: [CultureResource[], CloudflareMeta]) => {
        const urlCulture = route.params.culture as string | undefined;

        const isFormattedLikeACultureCode =
          !!urlCulture && CULTURE_CODE_REGEX.test(urlCulture);

        const isEnabledUrlCulture = cultures.some(
          (culture) => culture.cultureCode === urlCulture,
        );

        if (!isFormattedLikeACultureCode || !isEnabledUrlCulture) {
          const cookieCulture = cookieService.getItem(
            CookieStorageService.cookieCulture,
          );

          if (cookieCulture) {
            return of(
              navigateWithCulture(
                cookieCulture,
                isFormattedLikeACultureCode,
                route,
                store,
              ),
            );
          }

          if (cloudflareMeta && cloudflareMeta.country) {
            const cloudflareCulture = COUNTRY_CODE_TO_CULTURE[cloudflareMeta.country] ?? FALLBACK_CULTURE;
            cookieStorageService.setItem(
              CookieStorageService.cookieCulture,
              cloudflareCulture,
              180 * SECONDS_PER_DAY,
            );
            return of(
              navigateWithCulture(
                cloudflareCulture,
                isFormattedLikeACultureCode,
                route,
                store,
              ),
            );
          }

          geocodeService.checkPermissions().pipe()
            .subscribe((permission) => {
              if (permission.state === 'prompt' || permission.state === 'granted') {
                geocodeService.navigateToGeocodedCulture();
              }
            });

          return of(
            navigateWithCulture(
              FALLBACK_CULTURE,
              isFormattedLikeACultureCode,
              route,
              store,
            ),
          );
        }
        return of(true);
      },
    ),
  );
};

const navigateWithCulture = (
  cultureCode: string,
  isFormatted: boolean,
  route: ActivatedRouteSnapshot,
  store: Store,
): UrlTree => {
  const countryCode = DEFAULT_COUNTRY_CODE_BY_CULTURE[cultureCode ?? FALLBACK_CULTURE] as string;
  store.dispatch(userCountryGeolocated({countryCode}));
  
  const urlSegments: UrlSegment[] = [];

  if (isFormatted && route.params.culture) {
    urlSegments.push(new UrlSegment(route.params.culture as string, {}));
  } else {
    route.url.forEach((s) => urlSegments.push(s));
  }
  let firstChild = route.firstChild;

  while (firstChild) {
    firstChild?.url.forEach((segment) => urlSegments.push(segment));
    firstChild = firstChild.firstChild;
  }

  const path = new UrlSegment(cultureCode, {});
  if (!isFormatted) {
    urlSegments.unshift(path);
  } else {
    urlSegments[0] = path;
  }

  return createUrlTreeFromSnapshot(
    route.root,
    urlSegments.map((p) => p.path),
    route.queryParams,
    route.fragment,
  );
};